import { QUANTITY_NUMBER, TRACKING_EVENTS, TRACKING_EVENT_TYPES, TRACKING_PRICE } from "./const";
import type {
  CommonClickEventName,
  ItemListName,
  TrackingSendData,
  TypeProductDetail,
  ViaInfo,
  VideoCast,
  VideoOrientation,
  VideoSpeed,
  VideoStartMethod,
} from "./type";
import { runningInBrowser } from "~/const/browser";

type TrackingEventTypes = typeof TRACKING_EVENT_TYPES;

export const sendTrackingEvent = (data: TrackingSendData) => {
  if (!runningInBrowser) return;
  window.dataLayer = window.dataLayer || [];
  window.dmm_mkbase_ga_util = window.dmm_mkbase_ga_util || {};
  window.dmm_mkbase_ga_util.merge_common_data =
    window.dmm_mkbase_ga_util.merge_common_data ||
    function (data: TrackingSendData) {
      return data;
    };

  window.dataLayer.push(window.dmm_mkbase_ga_util.merge_common_data(data));
};

// ページ遷移でtrackingを発火する
export const sendTrackingPageView = (pageType: string | null) => {
  if (!window.i3 || !pageType || !window.dataLayer || !window.dmm_mkbase_ga_util?.merge_common_data) return;

  const data = {
    event: TRACKING_EVENTS.PAGE_VIEW,
    cloud_retail: {
      eventType: TRACKING_EVENT_TYPES.HOME_PAGE_VIEW,
    },
    manual_fire_event: true,
    page_type: pageType,
  };

  if (!window.dataLayer) return;

  window.dataLayer.push({ ecommerce: null });
  window.dataLayer.push(window.dmm_mkbase_ga_util.merge_common_data(data));
};

/** トラッキング送信（お気に入り追加） */
export const sendTrackingAddToWishlist = (productData: {
  seasonId: string;
  item_name: string;
  priceInfo: {
    /** 商品価格(税抜) */
    price: typeof TRACKING_PRICE;
  };
  itemVariant?: string;
}) => {
  const { seasonId, item_name, itemVariant } = productData;

  sendTrackingEvent({
    event: TRACKING_EVENTS.ADD_TO_WISHLIST,
    cloud_retail: {
      productDetails: [
        {
          product: {
            id: seasonId,
            item_name,
            item_variant: itemVariant,
            priceInfo: {
              price: TRACKING_PRICE,
            },
          },
          quantity: QUANTITY_NUMBER,
        },
      ],
    },
    wish_list_name: "wishlist",
  });
};

/** トラッキング送信（カート追加） */
export const sendTrackingAddToCart = (productData: { seasonId: string; item_name: string }) => {
  const { seasonId, item_name } = productData;

  sendTrackingEvent({
    event: TRACKING_EVENTS.ADD_TO_CART,
    cloud_retail: {
      eventType: "add-to-cart",
      productDetails: [
        {
          product: {
            id: seasonId,
            item_name,
            priceInfo: {
              price: TRACKING_PRICE,
            },
          },
          quantity: QUANTITY_NUMBER,
        },
      ],
    },
  });
};

/** トラッキング送信（商品選択） */
export const sendTrackingSelectItem = (videoData: {
  seasonId: string;
  itemListId: string | null;
  index: number | null;
  itemListName?: ItemListName;
  itemName?: string;
  viaViewRank?: number | null;
  trackingId?: string | null;
  testId?: string | null;
  requestId?: string | null;
  viaShowPosition: number;
}) => {
  const {
    seasonId,
    itemListName,
    index,
    itemListId,
    itemName,
    viaViewRank,
    trackingId = null,
    testId = null,
    requestId = null,
    viaShowPosition,
  } = videoData;
  sendTrackingEvent({
    event: TRACKING_EVENTS.SELECT_ITEM,
    cloud_retail: {
      productDetails: [
        {
          product: {
            id: seasonId,
            item_name: itemName,
            index,
            priceInfo: { price: TRACKING_PRICE },
          },
          quantity: QUANTITY_NUMBER,
        },
      ],
    },
    item_list_id: itemListId,
    item_list_name: itemListName,
    via_view_rank: viaViewRank,
    via_show_position: viaShowPosition,
    via_option: trackingId,
    via_info: requestId,
    misc: testId,
  });
};

/** トラッキング送信（ビデオ再生時） */
export const sendTrackingVideoStart = (data: {
  videoId: string;
  videoStartMethod: VideoStartMethod;
  videoUrl: string;
  duration: number;
  currentTime: number;
  videoTitle?: string;
  info?: string;
  targetId?: string;
}) => {
  sendTrackingEvent({
    event: TRACKING_EVENTS.VIDEO_START,
    video_id: data.videoId,
    video_provider: "dmm",
    video_url: data.videoUrl,
    video_title: data.videoTitle,
    video_duration: data.duration,
    video_current_time: data.currentTime,
    video_percent: Math.round((data.currentTime / data.duration) * 100),
    video_visible: true,
    video_start_method: data.videoStartMethod,
    via_info: data.info,
    via_target_id: data.targetId,
  });
};

/** トラッキング送信（商品リスト表示） */
export const sendTrackingViewItemList = (
  videoData: TypeProductDetail[] | TypeProductDetail,
  eventType: TrackingEventTypes["CATEGORY_PAGE_VIEW"],
  itemListId: string | null,
  itemListName: ItemListName | "related_videos" | "related_books",
  viaOption?: string | null,
  misc?: string | null,
  viaInfo?: string | null
) => {
  sendTrackingEvent({
    event: TRACKING_EVENTS.VIEW_ITEM_LIST,
    cloud_retail: {
      eventType,
      productDetails: videoData,
    },
    item_list_name: itemListName,
    item_list_id: itemListId,
    via_option: viaOption,
    misc: misc,
    via_info: viaInfo,
  });
};

/** トラッキング送信（商品詳細ページ表示） */
export const sendTrackingViewItem = (data: { id: string; name: string }) => {
  const { id, name } = data;

  sendTrackingEvent({
    event: TRACKING_EVENTS.VIEW_ITEM,
    cloud_retail: {
      eventType: TRACKING_EVENT_TYPES.DETAIL_PAGE_VIEW,
      productDetails: [
        {
          product: {
            id,
            item_name: name,
            priceInfo: { price: TRACKING_PRICE },
          },
          quantity: QUANTITY_NUMBER,
        },
      ],
    },
  });
};

/** トラッキング送信（検索実行） */
export const sendTrackingSearch = (data: {
  videoData: TypeProductDetail[];
  pageCategories: ["videos"] | ["programs"];
  searchQuery: string;
  searchHitCount: number;
  searchLimit: number;
  searchShowCount: number;
  // 栗原さんに確認して初期値はundefinedが帰る可能性があるが、期待値だったためundefinedを許容し、初期値にsortされていなかった場合にundefinedを許容する要件を満たすように、コードを変更した
  search_sort?: string;
}) => {
  sendTrackingEvent({
    event: TRACKING_EVENTS.SEARCH,
    cloud_retail: {
      eventType: TRACKING_EVENT_TYPES.SEARCH,
      productDetails: data.videoData,
      pageCategories: data.pageCategories,
      searchQuery: data.searchQuery,
    },
    search_hit_count: data.searchHitCount,
    search_page_number: 0,
    search_limit: data.searchLimit,
    search_show_count: data.searchShowCount,
    search_sort: data.search_sort,
    item_list_name: TRACKING_EVENTS.SEARCH,
  });
};
/** トラッキング送信（プロモーション選択 or プロモーション閲覧） */
export const sendTrackingSelectOrViewPromotion = (productData: {
  creativeName: "banner" | "shelf";
  promotionId: string;
  promotionName: string;
  locationId: string;
  viaViewRank?: number;
  isView?: boolean;
  misc?: string;
}) => {
  sendTrackingEvent({
    event: productData.isView ? TRACKING_EVENTS.VIEW_PROMOTION : TRACKING_EVENTS.SELECT_PROMOTION,
    creative_name: productData.creativeName,
    location_id: productData.locationId,
    promotion_id: productData.promotionId,
    promotion_name: productData.promotionName,
    via_view_rank: productData.viaViewRank,
    cloud_retail: {
      productDetails: {
        product: {
          id: productData.promotionId,
          item_name: productData.promotionName,
          priceInfo: {
            price: TRACKING_PRICE,
          },
        },
        quantity: QUANTITY_NUMBER,
      },
    },
    misc: productData.misc,
  });
};

/** トラッキング送信（汎用クリックイベント） */
export const sendTrackingClickCommon = (data: {
  clickEventName: CommonClickEventName;
  viaInfo?: ViaInfo;
  viaOption?: string;
  viaViewRank?: number;
  viaShowPosition?: "header";
  viaTargetId?: string;
  misc?: string;
}) => {
  sendTrackingEvent({
    event: TRACKING_EVENTS.CLICK_COMMON,
    common_click_event_name: data.clickEventName,
    via_info: data.viaInfo,
    via_option: data.viaOption,
    via_view_rank: data.viaViewRank,
    via_show_position: data.viaShowPosition,
    via_target_id: data.viaTargetId,
    misc: data.misc,
  });
};

/** トラッキング送信（動画停止イベント） */
export const sendTrackingVideoPause = (data: {
  videoId: string;
  videoUrl?: string;
  videoTitle?: string;
  videoDuration: number;
  videoCurrentTime: number;
  videoPercent: number;
  videoStartMethod: VideoStartMethod;
  videoQuality: string;
  videoConnectionLine?: string;
  videoSubtitles: string;
  videoSpeed: VideoSpeed;
  videoOrientation: VideoOrientation;
  videoCast?: VideoCast;
  videoAudio: string;
  videoIsAfterEnding?: string;
  viaInfo?: string;
  viaOption?: string;
  viaTargetId: string;
}) => {
  sendTrackingEvent({
    event: TRACKING_EVENTS.VIDEO_PAUSE,
    video_id: data.videoId,
    video_provider: "dmm",
    video_url: data.videoUrl,
    video_title: data.videoTitle,
    video_duration: data.videoDuration,
    video_current_time: data.videoCurrentTime,
    video_percent: data.videoPercent,
    video_visible: true,
    video_start_method: data.videoStartMethod,
    video_quality: data.videoQuality,
    video_connection_line: data.videoConnectionLine,
    video_subtitles: data.videoSubtitles,
    video_speed: data.videoSpeed,
    video_orientation: data.videoOrientation,
    video_cast: data.videoCast,
    video_audio: data.videoAudio,
    video_is_after_ending: data.videoIsAfterEnding,
    via_info: data.viaInfo,
    via_option: data.viaOption,
    via_target_id: data.viaTargetId,
  });
};

/** トラッキング送信（動画完了イベント） */
export const sendTrackingVideoComplete = (data: {
  videoId: string;
  videoUrl?: string;
  videoTitle?: string;
  videoDuration: number;
  videoCurrentTime: number;
  videoStartMethod: VideoStartMethod;
  videoQuality: string;
  videoConnectionLine?: string;
  videoSubtitles: string;
  videoSpeed: VideoSpeed;
  videoOrientation: VideoOrientation;
  videoCast?: VideoCast;
  videoAudio: string;
  videoIsAfterEnding?: string;
  viaInfo?: string;
  viaOption?: string;
  viaTargetId: string;
}) => {
  sendTrackingEvent({
    event: TRACKING_EVENTS.VIDEO_COMPLETE,
    video_id: data.videoId,
    video_provider: "dmm",
    video_url: data.videoUrl,
    video_title: data.videoTitle,
    video_duration: data.videoDuration,
    video_current_time: data.videoCurrentTime,
    video_percent: 100,
    video_visible: true,
    video_start_method: data.videoStartMethod,
    video_quality: data.videoQuality,
    video_connection_line: data.videoConnectionLine,
    video_subtitles: data.videoSubtitles,
    video_speed: data.videoSpeed,
    video_orientation: data.videoOrientation,
    video_cast: data.videoCast,
    video_audio: data.videoAudio,
    video_is_after_ending: data.videoIsAfterEnding,
    via_info: data.viaInfo,
    via_option: data.viaOption,
    via_target_id: data.viaTargetId,
  });
};
